import { z } from "zod";
import { Node, Edge } from "@xyflow/react";
import { AiAgentTemplate } from "@/model/interface/AiAgent";

export const templateSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  category: z.string().min(1),
  tier: z.string().min(1),
  voiceId: z.string().optional(),
  voiceProvider: z.string().optional(),
  defaultDeclaredOutcomes: z.array(z.string()).optional(),
  defaultSkills: z.array(z.string()).optional(),
  tools: z.record(z.string(), z.any()).optional(),
  orchestrator: z.string().min(1),
  llmProvider: z.string().optional(),
  llmModel: z.string().optional(),
  sttModel: z.string().optional(),
  sttProvider: z.string().optional(),
  realtimeVoice: z.string().optional(),
  aiDisclosureEnabled: z.boolean().nullable().optional(),
  defaultGreeting: z.string().optional(),
  defaultClosingMessage: z.string().optional(),
  demoAudioUrl: z.string().optional(),
  iconUrl: z.string().optional(),
  status: z.string().min(1),
  defaultVoiceStability: z.number().min(0).max(1).optional(),
  defaultVoiceSimilarityBoost: z.number().min(0).max(1).optional(),
  silencePromptTimeoutSec: z.number().int().min(1).max(60).optional(),
  silenceHangupTimeoutSec: z.number().int().min(5).max(300).optional(),
  silencePromptPhrase: z.string().optional(),
  waitPhraseTimeoutSec: z.number().int().min(5).max(120).optional(),
  temperature: z.number().min(0).max(1).optional(),
  endpointingMs: z.number().int().min(50).max(3000).optional(),
  turnHoldMs: z.number().int().min(0).max(3000).optional(),
  minCallDurationSec: z.number().int().min(0).max(600).optional(),
  maxDurationSeconds: z.number().int().min(0).max(7200).optional(),
});

export type TemplateFormValues = z.infer<typeof templateSchema>;

export const TEMPLATE_FORM_DEFAULTS: TemplateFormValues = {
  name: "",
  description: "",
  category: "CUSTOM",
  tier: "BASIC",
  voiceId: "",
  voiceProvider: "ELEVENLABS",
  defaultDeclaredOutcomes: [],
  defaultSkills: [],
  tools: {},
  orchestrator: "VAPI",
  llmProvider: "",
  llmModel: "",
  sttModel: "",
  sttProvider: "",
  realtimeVoice: "",
  aiDisclosureEnabled: null,
  defaultGreeting: "",
  defaultClosingMessage: "",
  demoAudioUrl: "",
  iconUrl: "",
  status: "DRAFT",
  defaultVoiceStability: undefined,
  defaultVoiceSimilarityBoost: undefined,
};

type DefaultConfig = Record<string, unknown> | null | undefined;

function readString(config: DefaultConfig, key: string): string | undefined {
  const value = config?.[key];
  return typeof value === "string" ? value : undefined;
}

function readNumber(config: DefaultConfig, key: string): number | undefined {
  const value = config?.[key];
  return typeof value === "number" ? value : undefined;
}

function readBoolean(config: DefaultConfig, key: string): boolean | undefined {
  const value = config?.[key];
  return typeof value === "boolean" ? value : undefined;
}

function readSeconds(config: DefaultConfig, key: string): number | undefined {
  const milliseconds = readNumber(config, key);
  if (milliseconds === undefined) {
    return undefined;
  }
  return Math.round(milliseconds / 1000);
}

export function templateToFormValues(template: AiAgentTemplate): TemplateFormValues {
  const defaultConfig = template.defaultConfig as DefaultConfig;
  return {
    name: template.name || "",
    description: template.description || "",
    category: template.category || "CUSTOM",
    tier: template.tier || "BASIC",
    voiceId: template.voiceId || "",
    voiceProvider: template.voiceProvider || "ELEVENLABS",
    defaultDeclaredOutcomes: template.defaultDeclaredOutcomes ?? [],
    defaultSkills: template.defaultSkills ?? [],
    tools: template.tools ?? {},
    orchestrator: template.orchestrator || "VAPI",
    llmProvider: template.llmProvider || "",
    llmModel: readString(defaultConfig, "llmModel") || "",
    sttModel: readString(defaultConfig, "sttModel") || "",
    sttProvider: readString(defaultConfig, "sttProvider") || "",
    realtimeVoice: readString(defaultConfig, "realtimeVoice") || "",
    aiDisclosureEnabled: readBoolean(defaultConfig, "aiDisclosureEnabled") ?? null,
    defaultGreeting: template.defaultGreeting || "",
    defaultClosingMessage: template.defaultClosingMessage || "",
    demoAudioUrl: template.demoAudioUrl || "",
    iconUrl: template.iconUrl || "",
    status: template.status || "DRAFT",
    defaultVoiceStability: readNumber(defaultConfig, "voiceStability"),
    defaultVoiceSimilarityBoost: readNumber(defaultConfig, "voiceSimilarityBoost"),
    silencePromptTimeoutSec: readSeconds(defaultConfig, "silencePromptTimeoutMs"),
    silenceHangupTimeoutSec: readSeconds(defaultConfig, "silenceHangupTimeoutMs"),
    silencePromptPhrase: readString(defaultConfig, "silencePromptPhrase"),
    waitPhraseTimeoutSec: readSeconds(defaultConfig, "waitPhraseTimeoutMs"),
    temperature: readNumber(defaultConfig, "temperature"),
    endpointingMs: readNumber(defaultConfig, "endpointingMs"),
    turnHoldMs: readNumber(defaultConfig, "turnHoldMs"),
    minCallDurationSec: readSeconds(defaultConfig, "minCallDurationMs"),
    maxDurationSeconds: readNumber(defaultConfig, "maxDurationSeconds"),
  };
}

export function templateConversationFlow(template: AiAgentTemplate): { nodes: Node[]; edges: Edge[] } | null {
  if (!template.conversationTree) {
    return null;
  }
  const tree = template.conversationTree as { nodes?: Node[]; edges?: Edge[] };
  if (!tree.nodes || !tree.edges) {
    return null;
  }
  return { nodes: tree.nodes, edges: tree.edges };
}

export function formValuesToTemplatePayload(
  values: TemplateFormValues,
  flowNodes: Node[],
  flowEdges: Edge[],
): Partial<AiAgentTemplate> {
  const cleanedNodes = flowNodes.map((node) => {
    const { onEdit, onDelete, ...restData } = node.data as Record<string, unknown>;
    return { ...node, data: restData };
  });

  const {
    defaultVoiceStability, defaultVoiceSimilarityBoost,
    llmModel, sttModel, sttProvider, realtimeVoice, aiDisclosureEnabled,
    silencePromptTimeoutSec, silenceHangupTimeoutSec, silencePromptPhrase, waitPhraseTimeoutSec,
    temperature, endpointingMs, turnHoldMs, minCallDurationSec, maxDurationSeconds,
    ...rest
  } = values;

  const defaultConfig: Record<string, unknown> = {};
  if (defaultVoiceStability !== undefined) {
    defaultConfig.voiceStability = defaultVoiceStability;
  }
  if (defaultVoiceSimilarityBoost !== undefined) {
    defaultConfig.voiceSimilarityBoost = defaultVoiceSimilarityBoost;
  }
  if (llmModel?.trim()) {
    defaultConfig.llmModel = llmModel.trim();
  }
  if (sttModel?.trim()) {
    defaultConfig.sttModel = sttModel.trim();
  }
  if (sttProvider?.trim()) {
    defaultConfig.sttProvider = sttProvider.trim();
  }
  if (realtimeVoice?.trim()) {
    defaultConfig.realtimeVoice = realtimeVoice.trim();
  }
  if (aiDisclosureEnabled !== null && aiDisclosureEnabled !== undefined) {
    defaultConfig.aiDisclosureEnabled = aiDisclosureEnabled;
  }
  if (silencePromptTimeoutSec !== undefined) {
    defaultConfig.silencePromptTimeoutMs = silencePromptTimeoutSec * 1000;
  }
  if (silenceHangupTimeoutSec !== undefined) {
    defaultConfig.silenceHangupTimeoutMs = silenceHangupTimeoutSec * 1000;
  }
  if (silencePromptPhrase?.trim()) {
    defaultConfig.silencePromptPhrase = silencePromptPhrase.trim();
  }
  if (waitPhraseTimeoutSec !== undefined) {
    defaultConfig.waitPhraseTimeoutMs = waitPhraseTimeoutSec * 1000;
  }
  if (temperature !== undefined) {
    defaultConfig.temperature = temperature;
  }
  if (endpointingMs !== undefined) {
    defaultConfig.endpointingMs = endpointingMs;
  }
  if (turnHoldMs !== undefined) {
    defaultConfig.turnHoldMs = turnHoldMs;
  }
  if (minCallDurationSec !== undefined) {
    defaultConfig.minCallDurationMs = minCallDurationSec * 1000;
  }
  if (maxDurationSeconds !== undefined) {
    defaultConfig.maxDurationSeconds = maxDurationSeconds;
  }

  return {
    ...rest,
    defaultConfig: Object.keys(defaultConfig).length > 0 ? defaultConfig : null,
    conversationTree: {
      nodes: cleanedNodes,
      edges: flowEdges,
    },
  };
}
